import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
} from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	forBusiness_graphics_choose,
	forBusiness_graphics_create,
	forBusiness_graphics_engage,
	forBusiness_graphics_matter,
	forBusiness_graphics_perfect,
	forBusiness_graphics_process,
	processAboveList,
	reviewsLanding,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import choose from '@/public/images/for-business/graphic/choose.webp';
import create from '@/public/images/for-business/graphic/create.webp';
import engage from '@/public/images/for-business/graphic/engage.webp';
import imgHero from '@/public/images/for-business/graphic/hero.webp';
import matter from '@/public/images/for-business/graphic/matter.webp';
import perfect from '@/public/images/for-business/graphic/perfect.webp';
import process from '@/public/images/for-business/graphic/process.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './GraphicsLanding.module.scss';

interface Props {}
const GraphicsLanding: React.FC<Props> = () => {
	const { t } = useTranslationWithLocale('branded-sm-graphics', 'en');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews', 'en');

	const goToProduct = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.webApp();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.BRANDED_GRAPHIC_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.BRANDED_GRAPHIC_LANDING },
	);

	useLandingShownEvent({
		type: LANDING_AMPLITUDE_EVENTS.BRANDED_GRAPHIC_LANDING,
	});
	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData_title')}
				subtitle={t('heroData_subtitle')}
				onClick={goToProduct}
				buttonText={t('getStarted')}
				classes={{
					mainTitle: styles['graphics__hero-title'],
					mainSubtitle: styles['graphics__hero-subtitle'],
				}}
			/>

			{/* matter */}
			<ImageTextSection
				title={t('matterData_mainTitle')}
				subtitle1={t('matterData_mainSubtitle1')}
				image={matter}
				specialListItem
				data={forBusiness_graphics_matter}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* engage */}
			<ImageTextSection
				title={t('engageData_mainTitle')}
				image={engage}
				specialListItem
				data={forBusiness_graphics_engage}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['graphics__engage-image-container'],
					list: styles['graphics__engage-list'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* choose */}
			<ImageTextSection
				title={t('chooseData_mainTitle')}
				subtitle1={t('chooseData_mainSubtitle1')}
				image={choose}
				data={forBusiness_graphics_choose}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* create */}
			<ImageTextSection
				title={t('createData_mainTitle')}
				image={create}
				data={forBusiness_graphics_create}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['graphics__create-image-container'],
					list: styles['graphics__create-list'],
					section: styles['graphics__create-section'],
				}}
			/>

			{/* process */}
			<ImageTextSection
				title={t('processData_mainTitle')}
				image={process}
				textsAboveList={processAboveList}
				data={forBusiness_graphics_process}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['graphics__process-section'],
				}}
			/>

			{/* perfect */}
			<ImageTextSection
				title={t('perfectData_mainTitle')}
				image={perfect}
				data={forBusiness_graphics_perfect}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['graphics__perfect-image-container'],
					list: styles['graphics__perfect-list'],
				}}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData_title1')}
				title_green={t('tryLiftData_greenText')}
				title2={t('tryLiftData_title2')}
				subtitle={t('tryLiftData_subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData_altText')}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					subtitleClass: styles['graphics__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default GraphicsLanding;
